import { Component, Input } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs';
import { LocalService } from '@app/@core/services/local.service';
import { AppService } from '@app/@core/services/app.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpService } from '@app/@core/services/http.service';

@Component({
  selector: 'app-dynamic-header-button',
  standalone: true,
  templateUrl: './dynamic-header-button.component.html',
  styleUrls: ['./dynamic-header-button.component.scss'],
  imports: [CommonModule,MatTooltipModule]
})
export class DynamicHeaderButtonComponent {
  @Input() data: any;
  showDeleteModal = false;

  get backButtonText(): string {
    return this.localstorage.getItem('backButtonText') ?? this.data?.backButtonText ?? 'Close';
  }
  get showDeleteButton(): boolean {
    return this.localstorage.getItem('showDeleteButton') === 'true' ? true : false;
  }

  constructor(
    private router: Router,
    private location: Location,
    private localstorage: LocalService,
    private http: HttpClient,
    private appService: AppService,
    private httpService : HttpService
  ) {}

  unlockApplication(): Observable<unknown> {
    try {
      const apiHost = this.localstorage.getItem('apiHost');
      const user = this.localstorage.getItem('user');
      const applicationID = this.localstorage.getItem('applicationID');

      if (!apiHost || !user || !applicationID) {
        return of(null);
      }

      const parsedUser = typeof user === 'string' ? JSON.parse(user) : user;
      const userId = parsedUser?.id;
      if (userId == null) {
        return of(null);
      }

      const apiUrl = `${apiHost}/queues/unlock-application`;
      const requestPayload = {
        userId: Number(userId),
        application_id: Number(applicationID)
      };

      return this.http.patch<{ success?: boolean; data?: { success?: boolean } }>(apiUrl, requestPayload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }).pipe(
        catchError((err) => {
          console.error('[unlock-application] Error calling unlock API:', err);
          return of(null);
        })
      );
    } catch {
      return of(null);
    }
  }

  /** Align with decision-role / payment close: dynamic-search by id, else `/pages/${name}` with no duplicate slashes. */
  private normalizePagesUrlFromName(name: string): string | null {
    const n = String(name ?? '').trim();
    if (!n) {
      return null;
    }
    if (n.startsWith('/pages/')) {
      return n;
    }
    const withoutLead = n.replace(/^\//, '');
    return `/pages/${withoutLead}`;
  }

  /**
   * Prefer session targets set when opening an application (queues, Lookup User, My App);
   * avoids staff `dashboardRoute` (e.g. /pages/rbac/users) overriding lookup return URLs.
   */
  private getSessionBackTargetUrl(): string | null {
    const grid = this.localstorage.getObj('backToGridPage');
    if (grid && typeof grid === 'object') {
      const id = (grid as { id?: unknown }).id;
      if (id != null && String(id).trim() !== '') {
        return `/pages/dynamic-search/search/${id}`;
      }
      const gridName = (grid as { name?: unknown }).name;
      if (gridName != null && String(gridName).trim() !== '') {
        return this.normalizePagesUrlFromName(String(gridName));
      }
    }

    const home = this.localstorage.getObj('backToHome');
    if (home && typeof home === 'object') {
      const homeName = (home as { name?: unknown }).name;
      if (homeName != null && String(homeName).trim() !== '') {
        return this.normalizePagesUrlFromName(String(homeName));
      }
    }

    return null;
  }

  private getDashboardRoute(): string | null {
    const raw = this.localstorage.getItem('dashboardRoute')?.trim();
    if (!raw) return null;

    if (raw.startsWith('http')) {
      try {
        return new URL(raw).pathname || null;
      } catch {
        return null;
      }
    }

    return raw.startsWith('/') ? raw : `/${raw}`;
  }

  private getApplicationMode(): string {
    const raw = this.localstorage.getItem('applicationmode');
    if (raw == null) {
      return '';
    }

    try {
      const parsed = JSON.parse(raw);
      return String(parsed ?? '').trim();
    } catch {
      return String(raw).trim();
    }
  }

  private doNavigateBack(): void {
    const route = this.getSessionBackTargetUrl() ?? this.getDashboardRoute();
    if (route) {
      this.router.navigateByUrl(route, { replaceUrl: true }).catch(() => {
        this.location.back();
      });
    } else {
      this.location.back();
    }
  }

  handleBackClick(): void {
    this.localstorage.setItem('showDeleteButton','false');
    if (this.getApplicationMode() === 'view') {
      this.doNavigateBack();
    } else {
      this.unlockApplication().pipe(
        finalize(() => this.doNavigateBack())
      ).subscribe();
    }
  }
  navigateToMessageInbox(): void {
    this.router.navigate(['/pages/message-center']);
    this.appService.addValue('currentPage', 'Message Center');
    const currentMenu = JSON.parse(this.localstorage.getItem('CURRENTMENU') || '{}');
    currentMenu.name = 'Message Center';
    this.localstorage.setItem('CURRENTMENU', JSON.stringify(currentMenu));
  }
  openDeleteModal() {
    this.showDeleteModal = true;
  }
  closeDeleteModal(){
    this.showDeleteModal = false;
  }
  deleteApplication(): void {
    if (this.localstorage.getItem('applicationID')) {
      const userId = Number(this.localstorage.getItem('id'));
      this.httpService
        .post('/registration/delete-application', {
          applicationId: Number(this.localstorage.getItem('applicationID')),
          userId: userId
        })
        .subscribe({
          next: _response => {
            this.localstorage.setItem('showDeleteButton','false');
            this.closeDeleteModal();
            this.handleBackClick();
          },
          error: error => {
            console.error('Failed to delete application:', error);
            alert('Failed to delete the application. Please try again.');
          }
        });
    }
  }
}



















